import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Card,
  CardPreview,
  Title3,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  ReOrderDotsVerticalRegular,
} from '@fluentui/react-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tendering, TenderingPart } from '../../../models/tendering';
import { Filter } from '../../../models/filters';
import TenderingAdminFilter from './TenderingAdminFilter';
import QuestionDialog from '../../dialogs/QuestionDialog';
// import { TenderingService } from '../../../../../services/tendering.service';
// import TenderingAdminFilter from './TenderingAdminFilter';
// import QuestionModal from '../../../../shared/QuestionModal';

interface TenderingAdminFiltersProps {
  tendering: Tendering;
  onTenderingChange: (tendering: Tendering) => void;
}

interface SortableFilterItemProps {
  filter: Filter;
  baseQuestionIDs: number[];
  tenderingParts: TenderingPart[];
  isBaseFilter: boolean;
  onDelete: (filter: Filter) => void;
  onUpdate: (filter: Filter) => void;
  onAddGroupFilter: (filter: Filter) => void;
  onDeleteGroupFilter: (group: Filter, filter: Filter) => void;
}

const SortableFilterItem: React.FC<SortableFilterItemProps> = ({
  filter,
  baseQuestionIDs,
  tenderingParts,
  isBaseFilter,
  onDelete,
  onUpdate,
  onAddGroupFilter,
  onDeleteGroupFilter,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: filter.filterID });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTextChange = (text: string) => {
    onUpdate({ ...filter, text });
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="flex items-start border-b border-gray-200 hover:bg-gray-50 p-4">
        <div
          {...attributes}
          {...listeners}
          className="absolute left-6 cursor-move mt-2"
        >
          <ReOrderDotsVerticalRegular className="w-12 h-12 text-gray-400" />
        </div>

        <div className="flex-1 ml-16">
          <Accordion collapsible>
            <AccordionItem value="1">
              <AccordionHeader>{filter.text}</AccordionHeader>
              <AccordionPanel>
                {filter.type === 'group' ? (
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold mb-2">Gruppnamn</h5>
                      <Input
                        className="w-full"
                        value={filter.text}
                        onChange={(e) => handleTextChange(e.target.value)}
                      />
                    </div>

                    {filter.filters && filter.filters.length > 0 && (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => {
                          const { active, over } = event;
                          if (over && active.id !== over.id) {
                            const oldIndex = filter.filters!.findIndex(
                              (f: Filter) => f.filterID === active.id
                            );
                            const newIndex = filter.filters!.findIndex(
                              (f: Filter) => f.filterID === over.id
                            );
                            const newFilters = arrayMove(
                              filter.filters!,
                              oldIndex,
                              newIndex
                            );
                            onUpdate({ ...filter, filters: newFilters });
                          }
                        }}
                      >
                        <SortableContext
                          items={filter.filters.map((f: Filter) => f.filterID)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {filter.filters.map((gfilter: Filter) => (
                              <SortableNestedFilterItem
                                key={gfilter.filterID}
                                filter={gfilter}
                                baseQuestionIDs={baseQuestionIDs}
                                tenderingParts={tenderingParts}
                                isBaseFilter={isBaseFilter}
                                onDelete={() => onDeleteGroupFilter(filter, gfilter)}
                                onUpdate={(updatedNested) => {
                                  const newFilters = (filter.filters || []).map((f: Filter) =>
                                    f.filterID === updatedNested.filterID ? updatedNested : f
                                  );
                                  onUpdate({ ...filter, filters: newFilters });
                                }}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}

                    <Button
                      appearance="primary"
                      onClick={() => onAddGroupFilter(filter)}
                      className="mr-4"
                    >
                      + SKAPA FILTER I GRUPP
                    </Button>
                  </div>
                ) : (
                  <div className="border border-gray-300 p-4 my-2 max-h-[40vh] overflow-y-auto">
                    <TenderingAdminFilter
                      filter={filter}
                      baseQuestionIDs={baseQuestionIDs}
                      tenderingParts={tenderingParts}
                      isBaseFilter={isBaseFilter}
                      onFilterChange={onUpdate}
                    />
                  </div>
                )}

                <Button
                  appearance="primary"
                  icon={<DeleteRegular />}
                  onClick={() => onDelete(filter)}
                  className="bg-red-600 hover:bg-red-700 mt-4"
                >
                  {filter.type === 'group'
                    ? 'RADERA GRUPP'
                    : isBaseFilter
                    ? 'Ta bort basfilter'
                    : 'RADERA FILTER'}
                </Button>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

interface SortableNestedFilterItemProps {
  filter: Filter;
  baseQuestionIDs: number[];
  tenderingParts: TenderingPart[];
  isBaseFilter: boolean;
  onDelete: () => void;
  onUpdate: (filter: Filter) => void;
}

const SortableNestedFilterItem: React.FC<SortableNestedFilterItemProps> = ({
  filter,
  baseQuestionIDs,
  tenderingParts,
  isBaseFilter,
  onDelete,
  onUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: filter.filterID });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="flex items-start border-b border-gray-200 hover:bg-gray-50 p-2">
        <div
          {...attributes}
          {...listeners}
          className="absolute left-2 cursor-move mt-2"
        >
          <ReOrderDotsVerticalRegular className="w-8 h-8 text-gray-400" />
        </div>

        <div className="flex-1 ml-12">
          <Accordion collapsible>
            <AccordionItem value="1">
              <AccordionHeader>{filter.text}</AccordionHeader>
              <AccordionPanel>
                <div className="border border-gray-300 p-4 my-4">
                  <p>Nested filter placeholder</p>
                  <TenderingAdminFilter
                    filter={filter}
                    baseQuestionIDs={baseQuestionIDs}
                    tenderingParts={tenderingParts}
                    isBaseFilter={isBaseFilter}
                    onFilterChange={onUpdate}
                  />
                </div>
                <Button
                  appearance="primary"
                  icon={<DeleteRegular />}
                  onClick={onDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  RADERA FILTER
                </Button>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

const TenderingAdminFilters: React.FC<TenderingAdminFiltersProps> = ({
  tendering,
  onTenderingChange,
}) => {
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<Filter | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const uuidv4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const getQuestionIDsFromFilters = (): number[] => {
    const questionIDs: number[] = [];

    tendering.properties.baseFilters.forEach((f: Filter) => {
      if (f.type === 'bool' || f.type === 'select') {
        if (f.separateQuestions && f.selections) {
          questionIDs.push(...f.selections.map((s: { questionID: number }) => s.questionID));
        } else if (f.questionID) {
          questionIDs.push(f.questionID);
        }
      }
    });

    return questionIDs;
  };

  const updateTendering = (updates: Partial<Tendering['properties']>) => {
    const updatedTendering = {
      ...tendering,
      properties: {
        ...tendering.properties,
        ...updates,
      },
    };
    onTenderingChange(updatedTendering);
  };

  // Base Filters
  const addBaseFilter = () => {
    const newFilter: Filter = {
      filterID: uuidv4(),
      type: 'bool',
      text: 'Nytt Basfilter',
      heading: '',
      tenderingPart: [],
    };
    updateTendering({
      baseFilters: [...tendering.properties.baseFilters, newFilter],
    });
  };

  const addBaseGroup = () => {
    const newGroup: Filter = {
      filterID: uuidv4(),
      type: 'group',
      text: 'Ny grupp',
      heading: '',
      filters: [],
      tenderingPart: [],
    };
    updateTendering({
      baseFilters: [...tendering.properties.baseFilters, newGroup],
    });
  };

  const deleteBaseFilter = (filter: Filter) => {
    updateTendering({
      baseFilters: tendering.properties.baseFilters.filter(
        (f: Filter) => f.filterID !== filter.filterID
      ),
    });
  };

  const updateBaseFilter = (updatedFilter: Filter) => {
    updateTendering({
      baseFilters: tendering.properties.baseFilters.map((f: Filter) =>
        f.filterID === updatedFilter.filterID ? updatedFilter : f
      ),
    });
  };

  const addBaseGroupFilter = (group: Filter) => {
    const newFilter: Filter = {
      filterID: uuidv4(),
      type: 'bool',
      text: 'Nytt Filter',
      heading: '',
      tenderingPart: [],
    };
    const updatedGroup = {
      ...group,
      filters: [...(group.filters || []), newFilter],
    };
    updateBaseFilter(updatedGroup);
  };

  const deleteGroupBaseFilter = (group: Filter, filter: Filter) => {
    const updatedGroup = {
      ...group,
      filters: (group.filters || []).filter((f: Filter) => f.filterID !== filter.filterID),
    };
    updateBaseFilter(updatedGroup);
  };

  const handleBaseFilterDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = tendering.properties.baseFilters.findIndex(
        (f: Filter) => f.filterID === active.id
      );
      const newIndex = tendering.properties.baseFilters.findIndex(
        (f: Filter) => f.filterID === over.id
      );
      updateTendering({
        baseFilters: arrayMove(
          tendering.properties.baseFilters,
          oldIndex,
          newIndex
        ),
      });
    }
  };

  // Advanced Filters
  const addFilter = () => {
    const newFilter: Filter = {
      filterID: uuidv4(),
      type: 'bool',
      text: 'Nytt Filter',
      heading: '',
      tenderingPart: [],
    };
    updateTendering({
      filters: [...tendering.properties.filters, newFilter],
    });
  };

  const addGroup = () => {
    const newGroup: Filter = {
      filterID: uuidv4(),
      type: 'group',
      text: 'Ny Grupp',
      heading: '',
      filters: [],
      tenderingPart: [],
    };
    updateTendering({
      filters: [...tendering.properties.filters, newGroup],
    });
  };

  const deleteFilter = (filter: Filter) => {
    updateTendering({
      filters: tendering.properties.filters.filter(
        (f: Filter) => f.filterID !== filter.filterID
      ),
    });
  };

  const updateFilter = (updatedFilter: Filter) => {
    updateTendering({
      filters: tendering.properties.filters.map((f: Filter) =>
        f.filterID === updatedFilter.filterID ? updatedFilter : f
      ),
    });
  };

  const addGroupFilter = (group: Filter) => {
    const newFilter: Filter = {
      filterID: uuidv4(),
      type: 'bool',
      text: 'Nytt Filter',
      heading: '',
      tenderingPart: [],
    };
    const updatedGroup = {
      ...group,
      filters: [...(group.filters || []), newFilter],
    };
    updateFilter(updatedGroup);
  };

  const deleteGroupFilter = (group: Filter, filter: Filter) => {
    const updatedGroup = {
      ...group,
      filters: (group.filters || []).filter((f: Filter) => f.filterID !== filter.filterID),
    };
    updateFilter(updatedGroup);
  };

  const handleFilterDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = tendering.properties.filters.findIndex(
        (f: Filter) => f.filterID === active.id
      );
      const newIndex = tendering.properties.filters.findIndex(
        (f: Filter) => f.filterID === over.id
      );
      updateTendering({
        filters: arrayMove(tendering.properties.filters, oldIndex, newIndex),
      });
    }
  };

  const handleQuestionSelect = (data: { selected: any[]; returnData?: any }) => {
    if (selectedFilter && data.selected) {
      const factMappings = data.selected.map((element) => ({
        permission: 'approval',
        questionID: element.questionID,
        tenderingPartID: element.tenderingPartID,
        heading: selectedFilter.heading || '',
        text: selectedFilter.text || '',
        section: '',
      }));

      const updatedTendering = {
        ...tendering,
        properties: {
          ...tendering.properties,
          factMapping: [
            ...(tendering.properties.factMapping || []),
            ...factMappings,
          ],
        },
      };

      onTenderingChange(updatedTendering);
    }
    setQuestionModalOpen(false);
  };

  const baseQuestionIDs = useMemo(() => getQuestionIDsFromFilters(), [tendering]);

  return (
    <Card>
      <CardPreview>
        <Title3>Filter</Title3>
      </CardPreview>
      <div className="overflow-y-auto p-2 space-y-8">
        {/* Base Filters Section */}
        <div>
          <h4 className="text-lg font-semibold pt-6 mb-4">Basfilter</h4>

        {tendering.properties.baseFilters.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleBaseFilterDragEnd}
          >
            <SortableContext
              items={tendering.properties.baseFilters.map((f: Filter) => f.filterID)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {tendering.properties.baseFilters.map((filter: Filter) => (
                  <SortableFilterItem
                    key={filter.filterID}
                    filter={filter}
                    baseQuestionIDs={baseQuestionIDs}
                    tenderingParts={tendering.tenderingParts}
                    isBaseFilter={true}
                    onDelete={deleteBaseFilter}
                    onUpdate={updateBaseFilter}
                    onAddGroupFilter={addBaseGroupFilter}
                    onDeleteGroupFilter={deleteGroupBaseFilter}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="flex justify-center gap-4 py-6">
          <Button appearance="primary" onClick={addBaseFilter} className="min-w-[182px]">
            + SKAPA BASFILTER
          </Button>
          <Button appearance="primary" onClick={addBaseGroup} className="min-w-[182px]">
            + SKAPA BASFILTER GRUPP
          </Button>
        </div>
      </div>

      {/* Advanced Filters Section */}
      <div>
        <h4 className="text-lg font-semibold pt-6 mb-4">Avancerade filter</h4>

        {tendering.properties.filters.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleFilterDragEnd}
          >
            <SortableContext
              items={tendering.properties.filters.map((f: Filter) => f.filterID)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {tendering.properties.filters.map((filter: Filter) => (
                  <SortableFilterItem
                    key={filter.filterID}
                    filter={filter}
                    baseQuestionIDs={baseQuestionIDs}
                    tenderingParts={tendering.tenderingParts}
                    isBaseFilter={false}
                    onDelete={deleteFilter}
                    onUpdate={updateFilter}
                    onAddGroupFilter={addGroupFilter}
                    onDeleteGroupFilter={deleteGroupFilter}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="flex justify-center gap-4 py-6">
          <Button appearance="primary" onClick={addFilter} className="min-w-[182px]">
            + SKAPA FILTER
          </Button>
          <Button appearance="primary" onClick={addGroup} className="min-w-[182px]">
            + SKAPA FILTER GRUPP
          </Button>
        </div>
      </div>

      {/* Question Modal */}
      {questionModalOpen && (
        <QuestionDialog
          isOpen={questionModalOpen}
          onClose={() => setQuestionModalOpen(false)}
          onSelect={handleQuestionSelect}
          tenderingParts={tendering.tenderingParts}
        />
      )}
      </div>
    </Card>
  );
};

export default TenderingAdminFilters;
